import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

/*
  View-only viewer for confidential PDFs (Resources menu).

  The browser never receives the PDF: each page arrives as a watermarked image
  from /api/documents/:slug/pages/:n, fetched with the user's token and shown
  through a blob URL, so no token ever appears in a URL.

  Route:  <Route path="/resources/documents/:slug" element={<DocumentViewer />} />
*/

const API_BASE = '/api/documents';
const ACK_KEY = 'di_confidentiality_ack';
const LOGIN_PATH = '/login'; // >>> ADAPT: your sign-in route

// >>> ADAPT: return the same Authorization header your other API calls use.
const authHeaders = () => {
  try {
    const token = localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch (e) {
    return {};
  }
};

const readAck = () => {
  try {
    return sessionStorage.getItem(ACK_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const writeAck = () => {
  try {
    sessionStorage.setItem(ACK_KEY, '1');
  } catch (e) {
    /* storage blocked: the notice simply shows again next visit */
  }
};

class HttpError extends Error {
  constructor(status) {
    super(`HTTP ${status}`);
    this.status = status;
  }
}

const request = async (url, options = {}) => {
  const res = await fetch(url, {
    ...options,
    headers: { ...authHeaders(), ...(options.headers || {}) },
  });
  if (!res.ok) throw new HttpError(res.status);
  return res;
};

// Blocks browser printing (print-to-PDF would otherwise bypass the viewer).
const PRINT_CSS = `
@media print {
  .di-doc-viewer { display: none !important; }
  body::after {
    content: 'Printing is disabled for confidential documents.';
    display: block;
    padding: 2rem;
    font: 16px sans-serif;
  }
}`;

const cardClass =
  'bg-white border border-slate-200 rounded-xl shadow-[6px_6px_0_rgba(24,127,115,0.12)]';

const focusRing =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#187F73]/40 rounded-sm';

/* ------------------------------------------------------------------ pieces */

// Serif bold title with the last word in teal (site title rule).
const Title = ({ text }) => {
  const words = text.split(' ');
  const last = words.pop();
  return (
    <h1 className="font-serif font-bold text-3xl sm:text-4xl text-slate-900 tracking-tight">
      {words.join(' ')}
      {words.length > 0 ? ' ' : ''}
      <span className="text-[#187F73]">{last}</span>
    </h1>
  );
};

const Shell = ({ title, children }) => (
  <main className="min-h-[70vh] bg-slate-50">
    <div className="max-w-3xl mx-auto px-4 py-12">
      <Title text={title} />
      <div className="mt-6">{children}</div>
    </div>
  </main>
);

const Notice = ({ heading, body, action }) => (
  <div className={`${cardClass} p-6`} role="alert">
    <h2 className="text-lg font-bold text-slate-900">{heading}</h2>
    <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
    {action && <div className="mt-5">{action}</div>}
  </div>
);

const primaryButton =
  'inline-flex items-center text-white text-sm font-bold bg-[#187F73] px-4 py-2 rounded-xl border border-[#187F73] transition-colors duration-200 hover:bg-[#15695F] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#187F73]/40';

// One page of the document: loads only when it nears the viewport.
const PageImage = ({ slug, page, total, aspect, onActive }) => {
  const holderRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [src, setSrc] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const el = holderRef.current;
    if (!el) return undefined;

    const near = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          near.disconnect();
        }
      },
      { rootMargin: '800px 0px' }
    );
    const active = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) onActive(page);
      },
      { threshold: 0.3 }
    );
    near.observe(el);
    active.observe(el);
    return () => {
      near.disconnect();
      active.disconnect();
    };
  }, [page, onActive]);

  useEffect(() => {
    if (!shouldLoad) return undefined;
    const controller = new AbortController();
    let objectUrl = null;
    setFailed(false);
    setSrc(null);

    request(`${API_BASE}/${slug}/pages/${page}`, { signal: controller.signal })
      .then((res) => res.blob())
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') setFailed(true);
      });

    return () => {
      controller.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [shouldLoad, slug, page, attempt]);

  return (
    <section
      ref={holderRef}
      aria-label={`Page ${page} of ${total}`}
      className={`${cardClass} overflow-hidden`}
      style={{ aspectRatio: `1 / ${aspect}` }}
    >
      {src && (
        <img
          src={src}
          alt={`Page ${page} of ${total}`}
          draggable={false}
          className="w-full h-full block pointer-events-none"
        />
      )}
      {!src && !failed && (
        <div className="w-full h-full flex items-center justify-center text-sm text-slate-400">
          Loading page {page}
        </div>
      )}
      {failed && (
        <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-sm text-slate-600">
          <span>Page {page} did not load.</span>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className={primaryButton}
          >
            Try again
          </button>
        </div>
      )}
    </section>
  );
};

/* -------------------------------------------------------------------- page */

export const DocumentViewer = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: 'loading' });
  const [acked, setAcked] = useState(readAck);
  const [ackBusy, setAckBusy] = useState(false);
  const [activePage, setActivePage] = useState(1);

  useEffect(() => {
    const controller = new AbortController();
    setState({ status: 'loading' });
    setActivePage(1);

    request(`${API_BASE}/${slug}/meta`, { signal: controller.signal })
      .then((res) => res.json())
      .then((meta) => setState({ status: 'ready', meta }))
      .catch((err) => {
        if (err.name === 'AbortError') return;
        const status = err instanceof HttpError ? err.status : 0;
        setState({ status: 'error', code: status });
      });

    return () => controller.abort();
  }, [slug]);

  const handleAcknowledge = async () => {
    setAckBusy(true);
    try {
      await request(`${API_BASE}/acknowledge`, { method: 'POST' });
      writeAck();
      setAcked(true);
    } catch (e) {
      setState({ status: 'error', code: e instanceof HttpError ? e.status : 0 });
    } finally {
      setAckBusy(false);
    }
  };

  const handleActive = useCallback((page) => setActivePage(page), []);

  /* ---- loading and error states ---- */

  if (state.status === 'loading') {
    return (
      <Shell title="Confidential Document">
        <p className="text-sm text-slate-500" role="status">
          Loading document
        </p>
      </Shell>
    );
  }

  if (state.status === 'error') {
    const { code } = state;
    if (code === 401) {
      return (
        <Shell title="Confidential Document">
          <Notice
            heading="Sign in to view this document"
            body="This document is available to approved reviewers only."
            action={
              <Link to={LOGIN_PATH} className={primaryButton}>
                Sign in
              </Link>
            }
          />
        </Shell>
      );
    }
    if (code === 403) {
      return (
        <Shell title="Confidential Document">
          <Notice
            heading="Your account does not have access"
            body="Access is granted by the Diagnostic Intelligence team. Contact us to request it."
            action={
              <Link to="/company/contact" className={primaryButton}>
                Request access
              </Link>
            }
          />
        </Shell>
      );
    }
    if (code === 404) {
      return (
        <Shell title="Confidential Document">
          <Notice
            heading="Document not found"
            body="This document has moved or is no longer available."
          />
        </Shell>
      );
    }
    return (
      <Shell title="Confidential Document">
        <Notice
          heading="The document could not be loaded"
          body="Check your connection and try again."
          action={
            <button type="button" onClick={() => window.location.reload()} className={primaryButton}>
              Reload
            </button>
          }
        />
      </Shell>
    );
  }

  const { meta } = state;

  /* ---- confidentiality notice ---- */

  if (!acked) {
    return (
      <Shell title={meta.title}>
        <div className={`${cardClass} p-6`}>
          <h2 className="text-lg font-bold text-slate-900">Before you open this document</h2>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            This document is confidential and shared with you for evaluation only. Do not copy,
            photograph, forward, or discuss its contents outside your organization without written
            permission from Diagnostic Intelligence.
          </p>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            Your email address and today&apos;s date appear on every page, and each time you open a
            document is recorded.
          </p>
          <button
            type="button"
            onClick={handleAcknowledge}
            disabled={ackBusy}
            className={`${primaryButton} mt-5 disabled:opacity-60`}
          >
            {ackBusy ? 'Saving' : 'Agree and open document'}
          </button>
        </div>
      </Shell>
    );
  }

  /* ---- viewer ---- */

  const pages = Array.from({ length: meta.pages }, (_, i) => i + 1);

  return (
    <div
      className="di-doc-viewer bg-slate-50 min-h-screen select-none"
      onContextMenu={(e) => e.preventDefault()}
      onDragStart={(e) => e.preventDefault()}
    >
      <style>{PRINT_CSS}</style>

      <div className="sticky top-16 z-40 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-4 h-12 flex items-center justify-between gap-4 text-sm">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className={`font-semibold text-slate-700 hover:text-[#187F73] transition-colors ${focusRing}`}
          >
            Back
          </button>
          <span className="hidden sm:block text-slate-500 truncate">
            Confidential, viewing as {meta.viewer}
          </span>
          <span className="font-semibold text-slate-700 whitespace-nowrap" aria-live="polite">
            Page {activePage} of {meta.pages}
          </span>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 pt-8 pb-16">
        <Title text={meta.title} />
        <div className="mt-8 space-y-6">
          {pages.map((n) => (
            <PageImage
              key={n}
              slug={slug}
              page={n}
              total={meta.pages}
              aspect={meta.aspect}
              onActive={handleActive}
            />
          ))}
        </div>
      </main>
    </div>
  );
};

export default DocumentViewer;
